// NotificationDropdown.js - Dropdown showing recent notifications

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheckDouble } from 'react-icons/fa';
import { useNotifications } from './NotificationContext';
import { AuthContext } from '../Provider';
import { ModernCard, CustomButton2 } from '../Templates';
import NotificationItem from './NotificationItem';
import Spinner from '../common/Spinner';
import './Notifications.css';
import { useContext } from 'react';

/**
 * NotificationDropdown component - displays recent notifications in a dropdown with infinite scroll
 */
const NotificationDropdown = () => {
  const { t } = useTranslation('common');
  // Get user from both contexts for debugging
  const { user: authUser } = useContext(AuthContext);
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    isDropdownOpen,
    markAllAsRead,
    refreshNotifications,
    emergencyRefresh,
    closeDropdown,
    fetchNotifications
  } = useNotifications();

  // State for pagination
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const scrollContainerRef = useRef(null);
  const refreshAttemptedRef = useRef(false);
  const ITEMS_PER_PAGE = 10;

  // Debug both user sources (reduce spam)
  // console.log('NotificationDropdown - AuthContext user:', { user: !!authUser, userId: authUser?.id });
  // console.log('NotificationDropdown - Direct user ID:', authUser?.id);

  // Reset pagination when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      setCurrentOffset(0);
      setHasMore(true);
      setLoadingMore(false);
      refreshAttemptedRef.current = false; // Reset refresh tracking
    }
  }, [isDropdownOpen]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && authUser?.id && !refreshAttemptedRef.current) {
      console.log('=== DROPDOWN OPENED ===');
      console.log('Dropdown opened, refreshing notifications for user:', authUser.id);
      
      refreshAttemptedRef.current = true; // Mark that we attempted refresh
      
      // Try normal refresh first
      refreshNotifications();
      
      // Fallback only if notifications are still empty after reasonable time
      const fallbackTimer = setTimeout(() => {
        if (notifications.length === 0) {
          console.log('🆘 No notifications loaded after refresh, using emergency fallback');
          emergencyRefresh(authUser.id);
        }
      }, 1000);
      
      // Cleanup timer
      return () => clearTimeout(fallbackTimer);
    }
  }, [isDropdownOpen, authUser?.id, refreshNotifications, emergencyRefresh, notifications.length]);

  // Load more notifications
  const loadMoreNotifications = useCallback(async () => {
    if (loadingMore || !hasMore || !authUser?.id) {
      return;
    }

    setLoadingMore(true);
    console.log('Loading more notifications, offset:', currentOffset + ITEMS_PER_PAGE);

    try {
      // Fetch next batch
      const newOffset = currentOffset + ITEMS_PER_PAGE;
      const newNotifications = await fetchNotifications(ITEMS_PER_PAGE, newOffset);
      
      // If we got fewer notifications than requested, we've reached the end
      if (!newNotifications || newNotifications.length < ITEMS_PER_PAGE) {
        console.log('Reached end of notifications, got:', newNotifications?.length || 0, 'expected:', ITEMS_PER_PAGE);
        setHasMore(false);
      }
      
      setCurrentOffset(newOffset);
      
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, authUser?.id, currentOffset, fetchNotifications]);

  // Handle scroll to load more
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // Load more when scrolled to 70% of the way down for better UX
    if (scrollPercentage > 0.7 && hasMore && !loadingMore) {
      loadMoreNotifications();
    }
  }, [hasMore, loadingMore, loadMoreNotifications]);

  // Don't render if dropdown is closed
  if (!isDropdownOpen) {
    return null;
  }

  // Get notifications for display
  const hasNotifications = notifications.length > 0;
  
  // Debug logging
  console.log('NotificationDropdown render:', {
    notificationsLength: notifications.length,
    hasNotifications,
    isLoading,
    error,
    unreadCount,
    hasMore,
    loadingMore
  });

  const handleMarkAllRead = async (e) => {
    e.preventDefault();
    await markAllAsRead();
  };

  const handleNotificationAction = (action, notification) => {
    console.log('Notification action:', action, notification);
    
    // Close dropdown when user clicks on a notification
    if (action === 'view') {
      closeDropdown();
    }
    // Keep dropdown open for accept/decline actions so user can see the result
  };

  return (
    <div className="NTS-notification-dropdown-overlay">
      <ModernCard className="NTS-notification-dropdown" hover={false}>
        {/* Header */}
        <div className="NTS-dropdown-header">
          <h4 className="NTS-dropdown-title">
            {t('notifications.title')}
            {unreadCount > 0 && (
              <span className="NTS-unread-badge">{unreadCount}</span>
            )}
          </h4>
          
          <div className="NTS-header-actions">
            {unreadCount > 0 && (
              <button
                className="NTS-mark-all-read-btn"
                onClick={handleMarkAllRead}
                title={t('notifications.mark_all_read')}
              >
                <FaCheckDouble />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div 
          className="NTS-dropdown-content" 
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {isLoading ? (
            <div className="NTS-dropdown-loading">
              <Spinner size="small" customText={t('notifications.loading')} />
            </div>
          ) : error ? (
            <div className="NTS-dropdown-error">
              <p className="NTS-error-message">{error}</p>
              <CustomButton2
                label={t('buttons.retry')}
                onClick={refreshNotifications}
              />
            </div>
          ) : hasNotifications ? (
            <>
              <div className="NTS-notification-list">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    showActions={true}
                    compact={true}
                    onActionComplete={handleNotificationAction}
                  />
                ))}
              </div>
              
              {/* Loading indicator for infinite scroll */}
              {loadingMore && (
                <div className="NTS-loading-more">
                  <Spinner size="small" customText={t('notifications.loading_more')} showText={false} />
                </div>
              )}
            </>
          ) : (
            <div className="NTS-no-notifications">
              <div className="NTS-no-notifications-icon">
                🔔
              </div>
              <p className="NTS-no-notifications-text">
                {t('notifications.no_notifications')}
              </p>
              <p className="NTS-no-notifications-subtext">
                {t('notifications.no_notifications_desc')}
              </p>
            </div>
          )}
        </div>
      </ModernCard>
    </div>
  );
};

export default NotificationDropdown;
